"use client";

import type { QueryKey, UseQueryOptions } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { useGraphqlQuery } from "@/lib/graphql/useGraphql";

type ExtraQueryOptions<TData> = Omit<UseQueryOptions<TData, Error, TData, QueryKey>, "queryKey" | "queryFn">;

type UsageScopeOptions<TData> = ExtraQueryOptions<TData> & {
  /** Optional project filter (server-side). Omit for org-wide / grant-scoped defaults. */
  projectId?: string | null;
};

// ─── Types ───────────────────────────────────────────────────────────────────

export type UsagePeriodInput = {
  preset?: "last_24h" | "last_7d" | "last_30d" | "current_period";
  from?: string;
  to?: string;
};

/** Bucket granularity for usage_buckets. Only DAY is supported today. */
export type UsageBucketInterval = "DAY";

/**
 * Combined range + granularity input for usage_buckets.
 *
 * Resolution priority:
 *   1. from + to      → explicit range, bucketed by interval
 *   2. from only      → from that date to now
 *   3. count (+ to)   → to − count × interval (e.g. last 7 days ending at to)
 *   4. count only     → last N intervals ending now
 *   5. (nothing)      → last 30 DAY buckets
 */
export type UsageBucketPeriodInput = {
  /** Explicit range start. Omit to use count-based lookback. */
  from?: string;
  /** Explicit range end. Defaults to now. */
  to?: string;
  /** Number of intervals to look back from to (or now). */
  count?: number;
  /** Bucket granularity. Defaults to DAY. */
  interval?: UsageBucketInterval;
};

export type UsageByModel = {
  model: string;
  llm_model_catalog_id: string | null;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  requests: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  input_tokens: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  output_tokens: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  cost_cents: string;
};

export type UsageSummary = {
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  requests: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  input_tokens: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  output_tokens: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  total_tokens: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  estimated_cost_cents: string;
  distinct_models: number;
  by_model: UsageByModel[];
};

export type UsageBucketModelBreakdown = {
  model: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  requests: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  tokens: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  cost_cents: string;
};

export type UsageBucket = {
  date: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  requests: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  tokens: string;
  /** BigInt scalar — serialized as a string over the wire. Use Number() before arithmetic. */
  cost_cents: string;
  by_model: UsageBucketModelBreakdown[];
};

export type UsageLedgerEvent = {
  id: string;
  occurred_at: string;
  model: string;
  project_id: string | null;
  project_name: string | null;
  api_key_id: string | null;
  api_key_label: string | null;
  input_tokens: number;
  output_tokens: number;
  cost_cents: number;
};

export type UsageLedgerConnection = {
  events: UsageLedgerEvent[];
  next_cursor: string | null;
  has_next_page: boolean;
};

export type UsageLedgerFilter = {
  model?: string;
  api_key_id?: string;
  project_id?: string;
  from?: string;
  to?: string;
};

// ─── Documents ───────────────────────────────────────────────────────────────

const usageSummaryDocument = gql`
  query UsageSummary($period: UsagePeriodInput!, $project_id: ID) {
    usage_summary(period: $period, project_id: $project_id) {
      requests
      input_tokens
      output_tokens
      total_tokens
      estimated_cost_cents
      distinct_models
      by_model {
        model
        llm_model_catalog_id
        requests
        input_tokens
        output_tokens
        cost_cents
      }
    }
  }
`;

const usageBucketsDocument = gql`
  query UsageBuckets($period: UsageBucketPeriodInput!, $project_id: ID) {
    usage_buckets(period: $period, project_id: $project_id) {
      date
      requests
      tokens
      cost_cents
      by_model {
        model
        requests
        tokens
        cost_cents
      }
    }
  }
`;

const usageLedgerDocument = gql`
  query UsageLedger($first: Int!, $after: String, $filter: UsageLedgerFilter) {
    usage_ledger(first: $first, after: $after, filter: $filter) {
      events {
        id
        occurred_at
        model
        project_id
        project_name
        api_key_id
        api_key_label
        input_tokens
        output_tokens
        cost_cents
      }
      next_cursor
      has_next_page
    }
  }
`;

// ─── Hooks ───────────────────────────────────────────────────────────────────

type UsageSummaryQuery = { usage_summary: UsageSummary };
type UsageBucketsQuery = { usage_buckets: UsageBucket[] };
type UsageLedgerQuery = { usage_ledger: UsageLedgerConnection };

export const useUsageSummary = (period: UsagePeriodInput, options?: UsageScopeOptions<UsageSummaryQuery>) => {
  const { projectId, ...queryOptions } = options ?? {};
  return useGraphqlQuery<UsageSummaryQuery, { period: UsagePeriodInput; project_id?: string }>({
    queryKey: ["usage-summary", period, projectId ?? null],
    document: usageSummaryDocument,
    variables: {
      period,
      ...(projectId ? { project_id: projectId } : {}),
    },
    retry: false,
    ...queryOptions,
  });
};

export const useUsageBuckets = (period: UsageBucketPeriodInput, options?: UsageScopeOptions<UsageBucketsQuery>) => {
  const { projectId, ...queryOptions } = options ?? {};
  return useGraphqlQuery<UsageBucketsQuery, { period: UsageBucketPeriodInput; project_id?: string }>({
    queryKey: ["usage-buckets", period, projectId ?? null],
    document: usageBucketsDocument,
    variables: {
      period,
      ...(projectId ? { project_id: projectId } : {}),
    },
    retry: false,
    ...queryOptions,
  });
};
export const useUsageLedger = (
  first: number,
  after?: string | null,
  filter?: UsageLedgerFilter | null,
  options?: ExtraQueryOptions<UsageLedgerQuery>,
) =>
  useGraphqlQuery<UsageLedgerQuery, { first: number; after?: string; filter?: UsageLedgerFilter }>({
    queryKey: ["usage-ledger", first, after ?? null, filter ?? null],
    document: usageLedgerDocument,
    variables: {
      first,
      ...(after ? { after } : {}),
      ...(filter ? { filter } : {}),
    },
    retry: false,
    ...options,
  });

// ─── Utils ───────────────────────────────────────────────────────────────────

/** Convert USD cents (number or BigInt string) to a formatted dollar string. */
export const formatCostUsd = (cents: string | number): string => `$${(Number(cents) / 100).toFixed(2)}`;
